import React, { useEffect, useState } from "react";
import {
    collection,
    doc,
    onSnapshot,
    runTransaction,
  } from "firebase/firestore";

import { db } from "../firebase";

import {
  INITIAL_AUCTION_CATEGORIES,
} from "./initialAuctionCategories";

export default function CategoryManagementPage() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [categoryListError, setCategoryListError] = useState("");
    const [categoryListConfirmed, setCategoryListConfirmed] = useState(false);

  const [initializing, setInitializing] = useState(false);
  const [initializationMessage, setInitializationMessage] = useState("");

  const [categoriesInitialized, setCategoriesInitialized] = useState(null);
  const [loadedCategoryVersion, setLoadedCategoryVersion] = useState(null);
  const [expectedCategoryCount, setExpectedCategoryCount] = useState(null);

  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryArabicName, setNewCategoryArabicName] = useState("");
  const [newCategoryIcon, setNewCategoryIcon] = useState("grid-outline");
  const [newCategoryOrder, setNewCategoryOrder] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [categoryFormMessage, setCategoryFormMessage] = useState("");

  const [updatingCategoryId, setUpdatingCategoryId] = useState(null);
  const [categoryStatusMessage, setCategoryStatusMessage] = useState("");

  const othersCategoryForOrder = categories.find(
    (category) => category.id === "others"
  );

  const suggestedCategoryOrder =
    othersCategoryForOrder &&
    Number.isSafeInteger(othersCategoryForOrder.order)
      ? othersCategoryForOrder.order
      : Math.max(
          0,
          ...categories
            .map((category) => category.order)
            .filter((order) => Number.isSafeInteger(order))
        ) + 1;

  const normalizedNewCategoryName = newCategoryName.trim().toLocaleLowerCase();
  const normalizedNewCategoryArabicName = newCategoryArabicName.trim();
  
  const newCategoryId = newCategoryName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  
  const duplicateCategoryId = categories.some(
    (category) => category.id === newCategoryId && newCategoryId.length > 0
  );

const duplicateEnglishCategory = categories.some(
  (category) =>
    typeof category.name === "string" &&
    category.name.trim().toLocaleLowerCase() === normalizedNewCategoryName &&
    normalizedNewCategoryName.length > 0
);

const duplicateArabicCategory = categories.some(
    (category) =>
      typeof category.arabicName === "string" &&
      category.arabicName.trim() === normalizedNewCategoryArabicName &&
      normalizedNewCategoryArabicName.length > 0
  );
  
  const isNewCategoryFormValid =
  newCategoryName.trim().length > 0 &&
  newCategoryArabicName.trim().length > 0 &&
  newCategoryIcon.trim().length > 0 &&
  newCategoryId.length > 0 &&
  !duplicateCategoryId &&
  !duplicateEnglishCategory &&
  !duplicateArabicCategory &&
  (newCategoryOrder.trim() === "" ||
    (Number.isSafeInteger(Number(newCategoryOrder)) &&
      Number(newCategoryOrder) >= 1));
  
  const missingInitialCategories = INITIAL_AUCTION_CATEGORIES.filter(
    (initialCategory) =>
      !categories.some(
        (existingCategory) => existingCategory.id === initialCategory.id
      )
  );

  const invalidInitialCategories = INITIAL_AUCTION_CATEGORIES.filter(
    (initialCategory) => {
      const existingCategory = categories.find(
        (category) => category.id === initialCategory.id
      );

      if (!existingCategory) return false;

      return (
        typeof existingCategory.name !== "string" ||
        !existingCategory.name.trim() ||
        typeof existingCategory.arabicName !== "string" ||
        !existingCategory.arabicName.trim() ||
        typeof existingCategory.icon !== "string" ||
        !existingCategory.icon.trim() ||
        !Number.isInteger(existingCategory.order) ||
        typeof existingCategory.active !== "boolean"
      );
    }
  );

  useEffect(() => {
    const configRef = doc(db, "appConfig", "auctionCategories");

    const unsubscribe = onSnapshot(
        configRef,
        { includeMetadataChanges: true },
        (configSnapshot) => {
          if (configSnapshot.metadata.fromCache) {
                setCategoriesInitialized(null);
                setLoadedCategoryVersion(null);
                setExpectedCategoryCount(null);
                setError(
                  "Category configuration is not yet confirmed by Firebase. Please check your connection."
                );
                return;
              }
  
          setError("");
  
          const configData = configSnapshot.exists()
            ? configSnapshot.data()
            : null;

        const validVersion =
          configData &&
          Number.isSafeInteger(configData.version) &&
          configData.version >= 0;

          setLoadedCategoryVersion(
            validVersion ? configData.version : null
          );
  
          const validCategoryCount =
            configData &&
            Number.isSafeInteger(configData.categoryCount) &&
            configData.categoryCount >= 0;
  
          setExpectedCategoryCount(
            validCategoryCount ? configData.categoryCount : null
          );
  
          if (
            !configData ||
            typeof configData.initialized !== "boolean" ||
            !validVersion ||
            (
              configData.categoryCount !== undefined &&
              !validCategoryCount
            ) ||
            (
              configData.initialized === true &&
              !validCategoryCount
            )
          ) {
            setCategoriesInitialized(null);
            setError(
              "Category initialization configuration is missing or invalid."
            );
            return;
          }

        setCategoriesInitialized(configData.initialized);
      },
      (firebaseError) => {
        console.error(
          "[CategoryManagement] Failed to monitor category configuration:",
          firebaseError
        );

        setCategoriesInitialized(null);
        setLoadedCategoryVersion(null);
        setExpectedCategoryCount(null);
        setError("Unable to monitor category configuration.");
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const categoriesQuery = collection(db, "auctionCategories");

    const unsubscribe = onSnapshot(
        categoriesQuery,
        { includeMetadataChanges: true },
        (snapshot) => {
            if (snapshot.metadata.fromCache) {
                setCategoryListConfirmed(false);
                setLoading(true);
                setCategoryListError(
                  "Waiting for Firebase to confirm the current category list."
                );
                return;
              }
      
              setCategoryListConfirmed(true);
      
              const categoryList = snapshot.docs
              .map((categoryDoc) => ({
                id: categoryDoc.id,
                ...categoryDoc.data(),
              }))
              .sort((a, b) => {
                if (a.id === "others") return 1;
                if (b.id === "others") return -1;

                const orderA = Number.isSafeInteger(a.order)
                  ? a.order
                  : Number.MAX_SAFE_INTEGER;

                const orderB = Number.isSafeInteger(b.order)
                  ? b.order
                  : Number.MAX_SAFE_INTEGER;

                return orderA - orderB || a.id.localeCompare(b.id);
              });

        setCategories(categoryList);
        setLoading(false);
        setCategoryListError("");
      },
      (firebaseError) => {
        console.error(
          "[CategoryManagement] Failed to load categories:",
          firebaseError
        );

        setCategoryListConfirmed(false);
        setCategoryListError("Unable to load categories from Firebase.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
}, []);

const handleInitializeCategories = async () => {
    if (
      initializing ||
      loading ||
      !categoryListConfirmed ||
      Boolean(error) ||
      Boolean(categoryListError) ||
      categoriesInitialized !== false
    ) {
      return;
    }
  
    if (invalidInitialCategories.length > 0) {
        setInitializationMessage(
          "Initialization stopped because existing categories contain invalid fields. Please review them before continuing."
        );
        return;
      }
  
      const confirmed = window.confirm(
    "Initialize the official X-AUCTIONS categories?\n\n" +
    "This will create missing category documents only.\n" +
    "Existing categories will not be overwritten.\n\n" +
    "Do you want to continue?"
  );

  if (!confirmed) return;

  setInitializing(true);
  setInitializationMessage("");

  try {
    console.log(
        "[CategoryManagement] Starting initialization transaction."
      );
  
      const result = await runTransaction(db, async (transaction) => {
        const configRef = doc(db, "appConfig", "auctionCategories");
  
        console.log(
          "[CategoryManagement] Reading initialization configuration..."
        );
  
        // Verify initialization status inside the transaction.
        const configSnapshot = await transaction.get(configRef);
  
        console.log(
          "[CategoryManagement] Configuration read completed.",
          {
            exists: configSnapshot.exists(),
            initialized: configSnapshot.data()?.initialized,
            version: configSnapshot.data()?.version,
          }
        );

      if (
        !configSnapshot.exists() ||
        configSnapshot.data().initialized !== false
      ) {
        throw new Error(
          "Category initialization is blocked because the configuration is missing or already initialized."
        );
      }

      const initializationVersion = configSnapshot.data().version;

      if (
        !Number.isSafeInteger(initializationVersion) ||
        initializationVersion < 0
      ) {
        throw new Error(
          "Category initialization is blocked because the configuration version is invalid."
        );
      }

      if (
        loadedCategoryVersion === null ||
        initializationVersion !== loadedCategoryVersion
      ) {
        throw new Error(
          "The category configuration has changed. Please refresh before initializing."
        );
      }

      const existingConfiguredCount = configSnapshot.data().categoryCount;

      if (
        existingConfiguredCount !== undefined &&
        (
          !Number.isSafeInteger(existingConfiguredCount) ||
          existingConfiguredCount < 0
        )
      ) {
        throw new Error(
          "Initialization stopped because the existing category count is invalid."
        );
      }

      if (
        existingConfiguredCount !== undefined &&
        existingConfiguredCount !== categories.length
      ) {
        throw new Error(
          "Initialization stopped because the configured category count does not match the current category list."
        );
      }

      const categoryRefs = INITIAL_AUCTION_CATEGORIES.map((category) =>
        doc(db, "auctionCategories", category.id)
      );

            // Firestore transactions must perform all reads before writes.
            console.log(
                "[CategoryManagement] Starting category document reads.",
                { requestedCount: categoryRefs.length }
              );
        
              const existingSnapshots = await Promise.all(
                categoryRefs.map((categoryRef) =>
                  transaction.get(categoryRef)
                )
              );
        
              console.log(
                "[CategoryManagement] Category document reads completed.",
                {
                  requestedCount: existingSnapshots.length,
                  existingCount: existingSnapshots.filter(
                    (snapshot) => snapshot.exists()
                  ).length,
                }
              );

      const officialCategoryIds = new Set(
        INITIAL_AUCTION_CATEGORIES.map((category) => category.id)
      );

      const unexpectedCategories = categories.filter(
        (category) => !officialCategoryIds.has(category.id)
      );

      if (unexpectedCategories.length > 0) {
        throw new Error(
          "Initialization stopped because unexpected category documents exist. Please review the category list first."
        );
      }

      const existingDocumentCount = existingSnapshots.filter(
        (snapshot) => snapshot.exists()
      ).length;

      if (existingDocumentCount !== categories.length) {
        throw new Error(
          "The category list changed or contains unexpected documents. Please refresh before initializing."
        );
      }

      const displayedCategoryIds = new Set(
        categories.map((category) => category.id)
      );

      const existingIdsMatch = existingSnapshots.every(
        (snapshot) =>
          snapshot.exists() === displayedCategoryIds.has(snapshot.id)
      );

      if (!existingIdsMatch) {
        throw new Error(
          "The existing category documents do not match the displayed category list. Please refresh before initializing."
        );
      }

      const invalidExistingCategories = existingSnapshots.filter(
        (snapshot) => {
          if (!snapshot.exists()) return false;

          const categoryData = snapshot.data();

          return (
            typeof categoryData.name !== "string" ||
            !categoryData.name.trim() ||
            typeof categoryData.arabicName !== "string" ||
            !categoryData.arabicName.trim() ||
            typeof categoryData.icon !== "string" ||
            !categoryData.icon.trim() ||
            !Number.isSafeInteger(categoryData.order) ||
            categoryData.order < 1 ||
            typeof categoryData.active !== "boolean"
          );
        }
      );

      if (invalidExistingCategories.length > 0) {
        throw new Error(
          "Initialization stopped because existing category documents contain invalid fields."
        );
      }

      const mismatchedExistingCategories = existingSnapshots.filter(
        (snapshot, index) => {
          if (!snapshot.exists()) return false;

          const existing = snapshot.data();
          const official = INITIAL_AUCTION_CATEGORIES[index];

          return (
            existing.name !== official.name ||
            existing.arabicName !== official.arabicName ||
            existing.icon !== official.icon ||
            existing.order !== official.order ||
            existing.active !== official.active
          );
        }
      );

      if (mismatchedExistingCategories.length > 0) {
        throw new Error(
          "Initialization stopped because existing categories do not match the official category definitions."
        );
      }

      let createdCount = 0;
      let existingCount = 0;

      INITIAL_AUCTION_CATEGORIES.forEach((category, index) => {
        if (existingSnapshots[index].exists()) {
          existingCount += 1;
          return;
        }

        const { id, ...categoryData } = category;

        transaction.set(categoryRefs[index], categoryData);
        createdCount += 1;
      });

      transaction.update(configRef, {
        initialized: true,
        version: initializationVersion + 1,
        categoryCount: INITIAL_AUCTION_CATEGORIES.length,
      });

      return { createdCount, existingCount };
    });

    setInitializationMessage(
        `Category initialization transaction succeeded. ` +
        `Created ${result.createdCount} categories and preserved ${result.existingCount} existing categories. ` +
        `Expected total: ${INITIAL_AUCTION_CATEGORIES.length}. ` +
        `Please verify the final category list in Firebase.`
      );
    } catch (firebaseError) {
        console.error(
          "[CategoryManagement] Initialization failed:",
          {
            code: firebaseError?.code || "unknown",
            message: firebaseError?.message || "Unknown error",
            error: firebaseError,
          }
        );
    
        setInitializationMessage(
          `Initialization failed: ${
            firebaseError?.message || "Unknown error"
          }`
        );
      } finally {
        console.log(
          "[CategoryManagement] Initialization attempt finished."
        );
    
        setInitializing(false);
      }
};

const handleAddCategory = async () => {
    // Do not allow saving while categories are loading,
    // initialization is incomplete, or another save is running.
    if (
      savingCategory ||
      initializing ||
      loading ||
      !categoryListConfirmed ||
      Boolean(error) ||
      Boolean(categoryListError) ||
      categoriesInitialized !== true
    ) {
    setCategoryFormMessage(
      "Categories must be fully initialized and loaded before adding a new category."
    );
    return;
  }

  // Validate all required fields and check for duplicates.
  if (!isNewCategoryFormValid) {
    setCategoryFormMessage(
      "Please enter valid category details and resolve any duplicate names or IDs."
    );
    return;
  }

    // Find the current position of the Others category.
    const othersCategory = categories.find(
        (category) => category.id === "others"
      );
    
      if (!othersCategory || !Number.isInteger(othersCategory.order)) {
        setCategoryFormMessage(
          "Cannot add a category because the Others category is missing or has an invalid display order."
        );
        return;
      }
    
      // A new category must always be inserted before Others.
      const requestedOrder =
        newCategoryOrder.trim() === ""
          ? othersCategory.order
          : Number(newCategoryOrder);
    
            // The final insertion order is calculated inside the
      // Firestore transaction using the latest category data.

      // Prepare the Firestore transaction.
      // No category documents will be written in this step.
      setSavingCategory(true);
      setCategoryFormMessage("");

      try {
        await runTransaction(db, async (transaction) => {
          const configRef = doc(db, "appConfig", "auctionCategories");
          const newCategoryRef = doc(db, "auctionCategories", newCategoryId);

          const categoryRefs = categories.map((category) =>
            doc(db, "auctionCategories", category.id)
          );

          // Firestore requires all transaction reads before writes.
          const configSnapshot = await transaction.get(configRef);
          const newCategorySnapshot = await transaction.get(newCategoryRef);

          const categorySnapshots = await Promise.all(
            categoryRefs.map((categoryRef) =>
              transaction.get(categoryRef)
            )
          );

          if (
            !configSnapshot.exists() ||
            configSnapshot.data().initialized !== true
          ) {
            throw new Error(
              "Category initialization is not complete."
            );
          }

          const categoryVersion = configSnapshot.data().version;
          const categoryCount = configSnapshot.data().categoryCount;

          if (
            !Number.isSafeInteger(categoryCount) ||
            categoryCount < 0
          ) {
            throw new Error(
              "Category configuration count is missing or invalid."
            );
          }

          if (
            !Number.isSafeInteger(categoryVersion) ||
            categoryVersion < 0
          ) {
            throw new Error(
              "Category configuration version is missing or invalid."
            );
          }

          if (
            loadedCategoryVersion === null ||
            categoryVersion !== loadedCategoryVersion
          ) {
            throw new Error(
              "The category configuration has changed since this page loaded. Please refresh before adding a category."
            );
          }

          if (categoryCount !== categorySnapshots.length) {
            throw new Error(
              "The category list is incomplete or outdated. Please refresh before saving."
            );
          }

          if (
            expectedCategoryCount === null ||
            categoryCount !== expectedCategoryCount
          ) {
            throw new Error(
              "The category count has changed since this page loaded. Please refresh before saving."
            );
          }

          if (newCategorySnapshot.exists()) {
            throw new Error(
              "A category with this ID already exists."
            );
          }

          if (categorySnapshots.some((snapshot) => !snapshot.exists())) {
            throw new Error(
              "The category list changed. Please refresh before saving."
            );
          }

          const latestCategories = categorySnapshots.map((snapshot) => ({
            id: snapshot.id,
            ...snapshot.data(),
          }));

          const latestDuplicateName = latestCategories.some((category) => {
            const englishName =
              typeof category.name === "string"
                ? category.name.trim().toLocaleLowerCase()
                : "";

            const arabicName =
              typeof category.arabicName === "string"
                ? category.arabicName.trim()
                : "";

            return (
              englishName === normalizedNewCategoryName ||
              arabicName === normalizedNewCategoryArabicName
            );
          });

          if (latestDuplicateName) {
            throw new Error(
              "A category with this English or Arabic name already exists."
            );
          }

          const latestOthersCategory = latestCategories.find(
            (category) => category.id === "others"
          );

          if (
            !latestOthersCategory ||
            !Number.isInteger(latestOthersCategory.order)
          ) {
            throw new Error(
              "The Others category is missing or has an invalid display order."
            );
          }

                    // Ensure the displayed category data still matches Firestore.
                    const categoryDataChanged = latestCategories.some(
                        (latestCategory, index) => {
                          const displayedCategory = categories[index];
            
                          return (
                            !displayedCategory ||
                            latestCategory.id !== displayedCategory.id ||
                            latestCategory.name !== displayedCategory.name ||
                            latestCategory.arabicName !== displayedCategory.arabicName ||
                            latestCategory.order !== displayedCategory.order ||
                            latestCategory.active !== displayedCategory.active
                          );
                        }
                      );
            
                      if (categoryDataChanged) {
                        throw new Error(
                          "Category information has changed. Please refresh the category list before saving."
                        );
                      }
            
                                           // Recalculate the insertion position using the
                      // latest category data from Firestore.
                      const latestInsertionOrder = Math.min(
                        Math.max(1, requestedOrder),
                        latestOthersCategory.order
                      );

                      // Identify categories that must move down to
                      // make room for the new category.
                      const categoriesToShift = latestCategories.filter(
                        (category) =>
                          Number.isInteger(category.order) &&
                          category.order >= latestInsertionOrder
                      );

                      // Verify that the category ordering is consistent.
                      const sortedCategories = [...latestCategories].sort(
                        (a, b) => a.order - b.order
                      );

                      const validCategoryOrders = sortedCategories.every(
                        (category, index) =>
                          Number.isSafeInteger(category.order) &&
                          category.order === index + 1
                      );

                      if (
                        !validCategoryOrders ||
                        sortedCategories[sortedCategories.length - 1]?.id !== "others"
                      ) {
                        throw new Error(
                          "Category ordering is inconsistent. Please review the category list before adding a new category."
                        );
                      }

                                            // Shift existing categories to make room.
                                            categoriesToShift.forEach((category) => {
                                                transaction.update(
                                                  doc(db, "auctionCategories", category.id),
                                                  { order: category.order + 1 }
                                                );
                                              });
                        
                                              // Create the new category without overwriting
                                              // any existing category document.
                                              transaction.set(newCategoryRef, {
                                                name: newCategoryName.trim(),
                                                arabicName: newCategoryArabicName.trim(),
                                                icon: newCategoryIcon.trim(),
                                                order: latestInsertionOrder,
                                                active: true,
                                              });
                        
                                              // Update the shared version in the same transaction.
                                              // Concurrent category changes will conflict and retry.
                                              transaction.update(configRef, {
                                                version: categoryVersion + 1,
                                                categoryCount: categoryCount + 1,
                                              });
        });

        setCategoryFormMessage(
            `Category "${newCategoryName.trim()}" was successfully added.`
          );
  
          setNewCategoryName("");
          setNewCategoryArabicName("");
          setNewCategoryIcon("grid-outline");
          setNewCategoryOrder("");
        } catch (firebaseError) {
            console.error(
              "[CategoryManagement] Category save failed:",
              firebaseError
            );
    
            setCategoryFormMessage(
              `Category save failed: ${
                firebaseError?.message || "Unknown error"
              }`
            );
        } finally {
            setSavingCategory(false);
          }
};

const handleToggleCategoryStatus = async (category) => {
  if (
    !category ||
    updatingCategoryId !== null ||
    savingCategory ||
    initializing ||
    loading ||
    !categoryListConfirmed ||
    Boolean(error) ||
    Boolean(categoryListError) ||
    categoriesInitialized !== true
  ) {
    setCategoryStatusMessage(
      "Category management is not ready. Please wait until the category list is fully loaded."
    );
    return;
  }

  if (category.id === "others") {
    setCategoryStatusMessage(
      'The "Others" category must remain active.'
    );
    return;
  }

  const currentlyActive = category.active !== false;
  const nextActive = !currentlyActive;

  const confirmed = window.confirm(
    `Are you sure you want to ${
      nextActive ? "activate" : "deactivate"
    } "${category.name}"?`
  );

  if (!confirmed) return;

  setUpdatingCategoryId(category.id);
  setCategoryStatusMessage("");

  try {
    await runTransaction(db, async (transaction) => {
      const configRef = doc(db, "appConfig", "auctionCategories");
      const categoryRef = doc(db, "auctionCategories", category.id);

      const configSnapshot = await transaction.get(configRef);
      const categorySnapshot = await transaction.get(categoryRef);

      if (
        !configSnapshot.exists() ||
        configSnapshot.data().initialized !== true
      ) {
        throw new Error("Category configuration is not initialized.");
      }

      if (!categorySnapshot.exists()) {
        throw new Error("This category no longer exists.");
      }

      const configData = configSnapshot.data();
      const currentVersion = configData.version;

      if (
        !Number.isSafeInteger(currentVersion) ||
        currentVersion < 0 ||
        loadedCategoryVersion !== currentVersion
      ) {
        throw new Error(
          "Category configuration has changed. Please refresh the page."
        );
      }

      if (
        !Number.isSafeInteger(configData.categoryCount) ||
        configData.categoryCount !== expectedCategoryCount ||
        configData.categoryCount !== categories.length
      ) {
        throw new Error(
          "Category count has changed. Please refresh the page."
        );
      }

      const latestCategory = categorySnapshot.data();

      if (
        latestCategory.name !== category.name ||
        latestCategory.arabicName !== category.arabicName ||
        latestCategory.order !== category.order ||
        latestCategory.active !== category.active
      ) {
        throw new Error(
          "Category information has changed. Please refresh the page."
        );
      }

      transaction.update(categoryRef, {
        active: nextActive,
      });

      transaction.update(configRef, {
        version: currentVersion + 1,
      });
    });

    setCategoryStatusMessage(
      `"${category.name}" was successfully ${
        nextActive ? "activated" : "deactivated"
      }.`
    );
  } catch (firebaseError) {
    console.error(
      "[CategoryManagement] Category status update failed:",
      firebaseError
    );

    setCategoryStatusMessage(
      `Category status update failed: ${
        firebaseError?.message || "Unknown error"
      }`
    );
  } finally {
    setUpdatingCategoryId(null);
  }
};

return (
    <div style={{ padding: 24 }}>
            <h2>Category Management</h2>
      <p>Manage the official English and Arabic auction categories for X-AUCTIONS.</p>

      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <button
          type="button"
          onClick={handleInitializeCategories}
          disabled={
            initializing ||
            loading ||
            !categoryListConfirmed ||
            Boolean(error) ||
            Boolean(categoryListError) ||
            categoriesInitialized !== false
          }
          style={{
            padding: "12px 20px",
            backgroundColor: "#172554",
            color: "#ffffff",
            border: "none",
            borderRadius: 8,
            cursor:
            initializing ||
            loading ||
            !categoryListConfirmed ||
            Boolean(error) ||
            Boolean(categoryListError) ||
            categoriesInitialized !== false
              ? "not-allowed"
              : "pointer",
          opacity:
            initializing ||
            loading ||
            !categoryListConfirmed ||
            Boolean(error) ||
            Boolean(categoryListError) ||
            categoriesInitialized !== false
              ? 0.5
              : 1,
            fontWeight: 600,
          }}
        >
          {initializing ? "Initializing Categories..." : "Initialize Missing Categories"}
        </button>

        {initializationMessage && (
          <p style={{ marginTop: 12 }}>
            {initializationMessage}
          </p>
                )}
                </div>
          
                <div
                  style={{
                    padding: 20,
                    marginBottom: 24,
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                  }}
                >
                  <h3 style={{ marginTop: 0 }}>Add New Category</h3>
          
                  <p>
                    Create a category with English and Arabic names.
                    Categories are added only after initialization and
                    validation checks are complete.
                  </p>
          
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 16,
                    }}
                  >
                    <label>
                      English Name
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(event) => setNewCategoryName(event.target.value)}
                        placeholder="e.g. Musical Instruments"
                        style={{ display: "block", width: "100%", marginTop: 6 }}
                      />
                    </label>
          
                    <label>
                      Arabic Name
                      <input
                        type="text"
                        dir="rtl"
                        value={newCategoryArabicName}
                        onChange={(event) => setNewCategoryArabicName(event.target.value)}
                        placeholder="الآلات الموسيقية"
                        style={{ display: "block", width: "100%", marginTop: 6 }}
                      />
                    </label>
          
                    <label>
                      Icon
                      <input
                        type="text"
                        value={newCategoryIcon}
                        onChange={(event) => setNewCategoryIcon(event.target.value)}
                        placeholder="e.g. musical-notes-outline"
                        style={{ display: "block", width: "100%", marginTop: 6 }}
                      />
                    </label>
          
                    <label>
                      Display Order
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={newCategoryOrder}
                        onChange={(event) => setNewCategoryOrder(event.target.value)}
                        placeholder={`Suggested: ${suggestedCategoryOrder}`}
                        style={{ display: "block", width: "100%", marginTop: 6 }}
                      />
                    </label>
                  </div>
          
                  {duplicateEnglishCategory && (
                    <p style={{ color: "#b91c1c", marginTop: 12 }}>
                      A category with this English name already exists.
                    </p>
                  )}

                  {duplicateArabicCategory && (
                    <p style={{ color: "#b91c1c", marginTop: 12 }}>
                      A category with this Arabic name already exists.
                    </p>
                  )}

<button
                    type="button"
                    onClick={handleAddCategory}
                    disabled={
                      savingCategory ||
                      initializing ||
                      loading ||
                      !categoryListConfirmed ||
                      Boolean(error) ||
                      Boolean(categoryListError) ||
                      categoriesInitialized !== true ||
                      Boolean(duplicateEnglishCategory) ||
                      Boolean(duplicateArabicCategory)
                    }
                    style={{
                      marginTop: 20,
                      padding: "12px 20px",
                      border: "none",
                      borderRadius: 8,
                      backgroundColor: "#172554",
                      color: "#ffffff",
                      opacity:
                        savingCategory ||
                        initializing ||
                        loading ||
                        !categoryListConfirmed ||
                        error ||
                        categoryListError ||
                        categoriesInitialized !== true ||
                        duplicateEnglishCategory ||
                        duplicateArabicCategory
                          ? 0.5
                          : 1,
                      cursor: "pointer",
                    }}
                  >
                    {savingCategory ? "Saving Category..." : "Save New Category"}
                  </button>
          
                  {categoryFormMessage && (
                    <p style={{ marginTop: 12 }}>{categoryFormMessage}</p>
                  )}
                </div>
          
                {loading && <p>Loading categories...</p>}
          
                {error && (
        <p style={{ color: "#b91c1c" }}>{error}</p>
      )}

      {categoryListError && (
        <p style={{ color: "#b91c1c" }}>
          {categoryListError}
        </p>
      )}

      {!loading && !error && (
        <>
                    <p>
            <strong>Total categories:</strong>{" "}
            {categories.length}
          </p>

          <div
            style={{
              padding: 16,
              marginTop: 16,
              marginBottom: 20,
              border: "1px solid #d1d5db",
              borderRadius: 8,
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              Initial Category Verification
            </h3>

            <p>
              <strong>Expected initial categories:</strong>{" "}
              {INITIAL_AUCTION_CATEGORIES.length}
            </p>

            <p>
              <strong>Existing initial categories:</strong>{" "}
              {INITIAL_AUCTION_CATEGORIES.length -
                missingInitialCategories.length}
            </p>

            <p>
              <strong>Missing initial categories:</strong>{" "}
              {missingInitialCategories.length}
            </p>

            <p>
              <strong>Categories with invalid fields:</strong>{" "}
              {invalidInitialCategories.length}
            </p>

            {invalidInitialCategories.length > 0 && (
              <p style={{ color: "#b91c1c" }}>
                Invalid category fields:{" "}
                {invalidInitialCategories
                  .map((category) => category.name)
                  .join(", ")}
              </p>
            )}

            {invalidInitialCategories.length === 0 && (
              <p style={{ color: "#166534" }}>
                All existing initial categories have valid required field types.
              </p>
            )}

<p>
              <strong>Initialization status:</strong>{" "}
              {categoriesInitialized === true
                ? "Initialized"
                : categoriesInitialized === false
                ? "Not initialized"
                : "Unknown"}
            </p>

            <p>
              <strong>Configured category count:</strong>{" "}
              {expectedCategoryCount === null
                ? "Not configured"
                : expectedCategoryCount}
            </p>

            <p>
              <strong>Currently loaded category documents:</strong>{" "}
              {categoryListConfirmed ? categories.length : "Not yet confirmed"}
            </p>

            <p>
              <strong>Category count verification:</strong>{" "}
              {!categoryListConfirmed || expectedCategoryCount === null
                ? "Pending"
                : expectedCategoryCount === categories.length
                ? "Matches"
                : "Mismatch"}
            </p>

            {missingInitialCategories.length > 0 && (
              <p style={{ color: "#92400e" }}>
                Missing:{" "}
                {missingInitialCategories
                  .map((category) => category.name)
                  .join(", ")}
              </p>
            )}

            {missingInitialCategories.length === 0 && (
              <p style={{ color: "#166534", fontWeight: 600 }}>
                All 26 initial category IDs are present.
              </p>
            )}
          </div>

          {categoryStatusMessage && (
            <p
              role="status"
              style={{
                marginTop: 16,
                padding: 12,
                borderRadius: 8,
                background: categoryStatusMessage.includes("failed") ||
                  categoryStatusMessage.includes("not ready")
                    ? "#fef2f2"
                    : "#f0fdf4",
                color: categoryStatusMessage.includes("failed") ||
                  categoryStatusMessage.includes("not ready")
                    ? "#991b1b"
                    : "#166534",
              }}
            >
              {categoryStatusMessage}
            </p>
          )}

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: 20,
            }}
          >
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 12 }}>
                  Order
                </th>
                <th style={{ textAlign: "left", padding: 12 }}>
                  English Name
                </th>
                <th style={{ textAlign: "left", padding: 12 }}>
                  Arabic Name
                </th>
                <th style={{ textAlign: "left", padding: 12 }}>
                  Icon
                </th>
                <th style={{ textAlign: "left", padding: 12 }}>
                  Status
                </th>
                <th style={{ textAlign: "left", padding: 12 }}>
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td style={{ padding: 12 }}>
                    {category.order}
                  </td>
                  <td style={{ padding: 12 }}>
                    {category.name}
                  </td>
                  <td
                    style={{
                      padding: 12,
                      direction: "rtl",
                      textAlign: "right",
                    }}
                  >
                    {category.arabicName}
                  </td>
                  <td style={{ padding: 12 }}>
                    {category.icon}
                  </td>
                  <td style={{ padding: 12 }}>
                    {category.active === false
                      ? "Inactive"
                      : "Active"}
                  </td>

                  <td style={{ padding: 12 }}>
                    <button
                      type="button"
                      onClick={() =>
                        handleToggleCategoryStatus(category)
                      }
                      disabled={
                        category.id === "others" ||
                        updatingCategoryId !== null ||
                        savingCategory ||
                        initializing ||
                        loading ||
                        !categoryListConfirmed ||
                        Boolean(error) ||
                        Boolean(categoryListError) ||
                        categoriesInitialized !== true
                      }
                      style={{
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        background:
                          category.active === false
                            ? "#dcfce7"
                            : "#fef3c7",
                        color:
                          category.active === false
                            ? "#166534"
                            : "#92400e",
                        fontWeight: 600,
                        cursor:
                          category.id === "others" ||
                          updatingCategoryId !== null
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      {updatingCategoryId === category.id
                        ? "Updating..."
                        : category.id === "others"
                        ? "Always Active"
                        : category.active === false
                        ? "Activate"
                        : "Deactivate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {categories.length === 0 && (
            <p>No official categories have been created yet.</p>
          )}
        </>
      )}
    </div>
  );
}